import { useCallback } from 'react'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { isGroupShape } from '@/lib/groups/group-kind'
import { selectedRootsAllowUngroup, ungroupBlockedMessage } from '@/lib/groups/ungroup-selection'
import { getShapeById } from '../helpers/shape-list'
import { isShapeSelectable, normalizeSelectableIdsForScope } from '../helpers/selection-scope'
import type { ShapeManagerStateApi } from './use-shape-manager-state'
import type { SelectionHistoryApi } from './use-selection-history'
import type { EditScopeRefsApi } from './use-edit-scope-refs'

export function useShapeUngrouping(
  ctx: ShapeManagerStateApi & SelectionHistoryApi & EditScopeRefsApi
) {
  const {
    editScopeGroupIdRef,
    onShapeCommitted,
    onShapeDeleted,
    onUserEdit,
    recordShapeSelectionHistory,
    selectedShapeIdsRef,
    setSelectedShapeIds,
    setShapeBounds,
    setShapes,
    shapesRef,
  } = ctx

  const canUngroupSelectedObjects = useCallback(() => {
    const shapes = shapesRef.current
    const selectedRoots = normalizeSelectableIdsForScope(
      selectedShapeIdsRef.current,
      shapes,
      editScopeGroupIdRef.current
    )

    return selectedRootsAllowUngroup(selectedRoots, shapes, editScopeGroupIdRef.current)
  }, [editScopeGroupIdRef, selectedShapeIdsRef, shapesRef])

  const getUngroupBlockedMessage = useCallback(() => {
    const shapes = shapesRef.current
    const selectedRoots = normalizeSelectableIdsForScope(
      selectedShapeIdsRef.current,
      shapes,
      editScopeGroupIdRef.current
    )

    return ungroupBlockedMessage(selectedRoots, shapes)
  }, [editScopeGroupIdRef, selectedShapeIdsRef, shapesRef])

  const ungroupSelectedObjects = useCallback((): string[] => {
    const list = shapesRef.current
    const selectedRoots = normalizeSelectableIdsForScope(
      selectedShapeIdsRef.current,
      list,
      editScopeGroupIdRef.current
    )

    if (!selectedRootsAllowUngroup(selectedRoots, list, editScopeGroupIdRef.current)) return []

    const groups = selectedRoots
      .map((id) => getShapeById(list, id))
      .filter(
        (shape): shape is ShapeData =>
          shape != null &&
          isGroupShape(shape) &&
          isShapeSelectable(shape, editScopeGroupIdRef.current) &&
          shape.locked !== true
      )

    const groupIds = new Set(groups.map((group) => group.id))
    const historyTxnId = `ungroup:${Array.from(groupIds).sort().join('|')}`
    const groupById = new Map(groups.map((group) => [group.id, group]))
    const groupIndexById = new Map(
      groups.map((group) => [group.id, list.findIndex((s) => s.id === group.id)])
    )
    const releasedChildren = list.filter(
      (shape) => shape.parentGroupId && groupIds.has(shape.parentGroupId)
    )
    const releasedIds = releasedChildren.map((shape) => shape.id)
    const childBeforeById = new Map(releasedChildren.map((shape) => [shape.id, { ...shape }]))

    setShapes((prev) => {
      const next = prev
        .filter((shape) => !groupIds.has(shape.id))
        .map((shape) =>
          shape.parentGroupId && groupIds.has(shape.parentGroupId)
            ? { ...shape, parentGroupId: undefined }
            : shape
        )

      shapesRef.current = next
      return next
    })

    setShapeBounds((prev) => {
      const next = { ...prev }

      for (const groupId of groupIds) {
        delete next[groupId]
      }

      return next
    })

    const nextSelection = normalizeSelectableIdsForScope(
      releasedIds,
      shapesRef.current,
      editScopeGroupIdRef.current
    )

    selectedShapeIdsRef.current = nextSelection
    setSelectedShapeIds(nextSelection)
    recordShapeSelectionHistory(selectedRoots, nextSelection, {
      source: 'command',
      gesture: 'replace',
      historyTxnId,
    })
    onUserEdit?.([...groupIds, ...releasedIds])

    const nextShapes = shapesRef.current

    for (const childId of releasedIds) {
      const beforeShape = childBeforeById.get(childId)
      const afterShape = nextShapes.find((shape) => shape.id === childId)

      if (!beforeShape || !afterShape) continue
      onShapeCommitted?.(afterShape, 'update', {
        before: { parentGroupId: beforeShape.parentGroupId },
        after: { parentGroupId: undefined },
        historyTxnId,
      })
    }

    for (const groupId of groupIds) {
      const group = groupById.get(groupId)

      if (group) {
        const released = list.filter((s) => s.parentGroupId === groupId)
        const ungroupNestedChildGroupIds = released.filter((s) => isGroupShape(s)).map((s) => s.id)
        const ungroupReleasedNonGroupChildIds = released
          .filter((s) => !isGroupShape(s))
          .map((s) => s.id)

        onShapeDeleted?.(group, {
          historyTxnId,
          historyInsertIndex: groupIndexById.get(groupId),
          ungroupNestedChildGroupIds,
          ungroupReleasedNonGroupChildIds,
        })
      }
    }

    return nextSelection
  }, [
    onShapeCommitted,
    onShapeDeleted,
    onUserEdit,
    recordShapeSelectionHistory,
    editScopeGroupIdRef,
    selectedShapeIdsRef,
    setSelectedShapeIds,
    setShapeBounds,
    setShapes,
    shapesRef,
  ])

  return { canUngroupSelectedObjects, getUngroupBlockedMessage, ungroupSelectedObjects }
}

export type ShapeUngroupingApi = ReturnType<typeof useShapeUngrouping>
